import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import {
	listCustomProperties,
	updateCustomProperties,
	createCustomProperties,
} from '../../../actions/profileActions';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import {
	IconButton,
	makeStyles,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@material-ui/core';
import { Add, Close, Delete } from '@material-ui/icons';

import IconsStore from '../../../reusable/IconsStore/IconsStore';
import { IconsList } from '../../../reusable/IconsStore/IconsList';
import ConfirmationDialog from '../../../reusable/ConfirmationDialog/ConfirmationDialog';

interface MultiSelectItem {
	value: string;
	name: string;
}

interface CustomProperty {
	order: number;
	nameInEditor: string;
	nameInApi: string;
	type: string;
	inMedia: boolean;
	inCatalog: boolean;
	inPlaylist: boolean;
	isRequired: boolean;
	multiSelect?: MultiSelectItem[];
}

interface CustomPropertiesProps {}

const styles = makeStyles({
    table: {
        minWidth: 650,
        boxShadow: 'none',
        fontSize: '12px',
    },
    tableCell: {
        fontWeight: 'bold',
        fontSize: '12px',
    },
    tableCellBody: {
        fontSize: '12px',
    },
    tableCellActions: {
        fontWeight: 'bold',
        fontSize: '12px',
        width: '80px',
    },
    container: {
        maxHeight: 'calc(100% - 100px)',
    }
});

const CustomProperties: React.FC<CustomPropertiesProps> = () => {
	const [customProperties, setCustomProperties] = useState<CustomProperty[]>([]);
	const [currentNameInEditor, setCurrentNameInEditor] = useState('');
	const [currentNameInApi, setCurrentNameInApi] = useState('');
	const [currentType, setCurrentType] = useState('string');
	const [currentInMedia, setCurrentInMedia] = useState(true);
	const [currentInCatalog, setCurrentInCatalog] = useState(false);
	const [currentInPlaylist, setCurrentInPlaylist] = useState(false);
	const [currentIsRequired, setCurrentIsRequired] = useState(false);
	const [currentMultiSelect, setCurrentMultiSelect] = useState<MultiSelectItem>({ value: '', name: '' });
	const [currentMultiSelectArray, setCurrentMultiSelectArray] = useState<MultiSelectItem[]>([]);
	const [nameInApiExists, setNameInApiExists] = useState(false);
	const [showNewPropertyDialog, setShowNewPropertyDialog] = useState(false);
	const [showEditPropertyDialog, setShowEditPropertyDialog] = useState(false);
	const [currentlySelectedItem, setCurrentlySelectedItem] = useState('');
	const [customPropertyToDelete, setCustomPropertyToDelete] = useState('');
	const [customPropertyToEdit, setCustomPropertyToEdit] = useState<CustomProperty>({} as CustomProperty);
	const [isCustomPropertyCreate, setIsCustomPropertyCreate] = useState(false);
	const [deleteCustomPropertyDialogStatus, setDeleteCustomPropertyDialogStatus] = useState(false);

    const { t } = useTranslation();
    const classes = styles();
    const dispatch = useDispatch() as any;
    const { defaultAccountId } = useSelector((state: any) => state.session);

	const getCustomPropertiesArray = useCallback((data: any): CustomProperty[] => {
		let result: CustomProperty[] = [];

		let mediaProps = data.properties.media.properties == null ? [] : data.properties.media.properties;
		let mediaProperties: [string, any][] = [];
		for (let i in mediaProps) {
			mediaProperties.push([i, mediaProps[i]]);
		}

		let catalogProps = data.properties.catalog.properties == null ? [] : data.properties.catalog.properties;
		let catalogProperties: [string, any][] = [];
		for (let i in catalogProps) {
			catalogProperties.push([i, catalogProps[i]]);
		}

		let playlistProps = data.properties.playlist.properties == null ? [] : data.properties.playlist.properties;
		let playlistProperties: [string, any][] = [];
		for (let i in playlistProps) {
			playlistProperties.push([i, playlistProps[i]]);
		}

		let requiredMedia = data.properties.media.required == null ? [] : data.properties.media.required;
		let requiredCatalog = data.properties.catalog.required == null ? [] : data.properties.catalog.required;
		let requiredPlaylist = data.properties.playlist.required == null ? [] : data.properties.playlist.required;

		let allRequired: [string, any][] = [];
		let allReq = Object.assign(requiredMedia, requiredPlaylist, requiredCatalog);
		for (let i in allReq) {
			allRequired.push([i, allReq[i]]);
		}

		let allProperties: [string, any][] = [];
		if (mediaProps != null || catalogProps != null || playlistProps != null) {
			let allProps = Object.assign(mediaProps, catalogProps, playlistProps);
			for (let i in allProps) {
				allProperties.push([i, allProps[i]]);
			}
		}

		for (let i = 0; i < allProperties.length; i++) {
			let checkArray = allProperties[i][1].enum;
			let tempObj: CustomProperty;
			if (Array.isArray(checkArray) && checkArray.length) {
				let tempMultiSelectArray = allProperties[i][1].enum;
				let resultArray: MultiSelectItem[] = [];
				let tempItem: MultiSelectItem;
				tempMultiSelectArray.map((item: any) => {
					tempItem = { value: Object.keys(item)[0], name: item[Object.keys(item)[0]] };
					resultArray.push(tempItem);
				});
				tempObj = {
					order: i + 1,
					nameInEditor: allProperties[i][1].title,
					nameInApi: allProperties[i][0],
					type: allProperties[i][1].type,
					inMedia: mediaProperties.some((data) => data.includes(allProperties[i][0])),
					inCatalog: catalogProperties.some((data) => data.includes(allProperties[i][0])),
					inPlaylist: playlistProperties.some((data) => data.includes(allProperties[i][0])),
					isRequired: allRequired.some((data) => data.includes(allProperties[i][0])),
					multiSelect: resultArray,
				};
			} else {
				tempObj = {
					order: i + 1,
					nameInEditor: allProperties[i][1].title,
					nameInApi: allProperties[i][0],
					type: allProperties[i][1].type,
					inMedia: mediaProperties.some((data) => data.includes(allProperties[i][0])),
					inCatalog: catalogProperties.some((data) => data.includes(allProperties[i][0])),
					inPlaylist: playlistProperties.some((data) => data.includes(allProperties[i][0])),
					isRequired: allRequired.some((data) => data.includes(allProperties[i][0])),
				};
			}
			result.push(tempObj);
		}

		return result;
	}, []);

	const getCustomPropertiesJSON = useCallback((data: CustomProperty[]) => {
		let catalogJSON: any = {};
		let catalogReq: string[] = [];
		for (let i = 0; i < data.length; i++) {
			if (data[i].inCatalog === true) {
				let tempObj: any;
				let checkArray = data[i].multiSelect;
				if (Array.isArray(checkArray) && checkArray.length) {
					let tempMultiSelectArray = data[i].multiSelect!;
					let resultArray: any[] = [];
					let tempItem: any;
					tempMultiSelectArray.map((item) => {
						tempItem = { [item.value]: item.name };
						resultArray.push(tempItem);
					});
					tempObj = {
						type: data[i].type,
						description: data[i].nameInEditor,
						title: data[i].nameInEditor,
						enum: resultArray,
					};
				} else {
					tempObj = { type: data[i].type, description: data[i].nameInEditor, title: data[i].nameInEditor };
				}

				let tempresult = { [data[i].nameInApi]: tempObj };
				catalogJSON = Object.assign(catalogJSON, tempresult);

				if (data[i].isRequired === true) {
					let temp = data[i].nameInApi;
					catalogReq.push(temp);
				}
			}
		}

		let mediaJSON: any = {};
		let mediaReq: string[] = [];
		for (let i = 0; i < data.length; i++) {
			if (data[i].inMedia === true) {
				let tempObj: any;
				let checkArray = data[i].multiSelect;
				if (Array.isArray(checkArray) && checkArray.length) {
					let tempMultiSelectArray = data[i].multiSelect!;
					let resultArray: any[] = [];
					let tempItem: any;
					tempMultiSelectArray.map((item) => {
						tempItem = { [item.value]: item.name };
						resultArray.push(tempItem);
					});
					tempObj = {
						type: data[i].type,
						description: data[i].nameInEditor,
						title: data[i].nameInEditor,
						enum: resultArray,
					};
				} else {
					tempObj = { type: data[i].type, description: data[i].nameInEditor, title: data[i].nameInEditor };
				}
				let tempresult = { [data[i].nameInApi]: tempObj };
				mediaJSON = Object.assign(mediaJSON, tempresult);

				if (data[i].isRequired === true) {
					let temp = data[i].nameInApi;
					mediaReq.push(temp);
				}
			}
		}

		let playlistJSON: any = {};
		let playlistReq: string[] = [];
		for (let i = 0; i < data.length; i++) {
			if (data[i].inPlaylist === true) {
				let tempObj: any;
				let checkArray = data[i].multiSelect;
				if (Array.isArray(checkArray) && checkArray.length) {
					let tempMultiSelectArray = data[i].multiSelect!;
					let resultArray: any[] = [];
					let tempItem: any;
					tempMultiSelectArray.map((item) => {
						tempItem = { [item.value]: item.name };
						resultArray.push(tempItem);
					});
					tempObj = {
						type: data[i].type,
						description: data[i].nameInEditor,
						title: data[i].nameInEditor,
						enum: resultArray,
					};
				} else {
					tempObj = { type: data[i].type, description: data[i].nameInEditor, title: data[i].nameInEditor };
				}
				let tempresult = { [data[i].nameInApi]: tempObj };
				playlistJSON = Object.assign(playlistJSON, tempresult);

				if (data[i].isRequired === true) {
					let temp = data[i].nameInApi;
					playlistReq.push(temp);
				}
			}
		}

		let catalogSchema: any;
		if (Array.isArray(catalogReq) && catalogReq.length) {
			catalogSchema = {
				type: 'object',
				id: '#catalog',
				properties: catalogJSON,
				additionalProperties: false,
				required: catalogReq,
			};
		} else {
			catalogSchema = {
				type: 'object',
				id: '#catalog',
				properties: catalogJSON,
				additionalProperties: false,
			};
		}

		let mediaSchema: any;
		if (Array.isArray(mediaReq) && mediaReq.length) {
			mediaSchema = {
				type: 'object',
				id: '#media',
				properties: mediaJSON,
				additionalProperties: false,
				required: mediaReq,
			};
		} else {
			mediaSchema = {
				type: 'object',
				id: '#media',
				properties: mediaJSON,
				additionalProperties: false,
			};
		}

		let playlistSchema: any;
		if (Array.isArray(playlistReq) && playlistReq.length) {
			playlistSchema = {
				type: 'object',
				id: '#playlist',
				properties: playlistJSON,
				additionalProperties: false,
				required: playlistReq,
			};
		} else {
			playlistSchema = {
				type: 'object',
				id: '#playlist',
				properties: playlistJSON,
				additionalProperties: false,
			};
		}

		let metadataSchema = {
			$schema: 'http://json-schema.org/draft-04/schema#',
			id: '#root',
			type: 'object',
			properties: {
				catalog: catalogSchema,
				media: mediaSchema,
				playlist: playlistSchema,
			},
		};

		return metadataSchema;
	}, []);

	const listCustomPropertiesHandler = useCallback(
		(accountId: string) => {
			dispatch(listCustomProperties(accountId)).then((data: any) => {
				if (data) {
					setCustomProperties(getCustomPropertiesArray(data));
				}
				if (data === undefined) {
					setIsCustomPropertyCreate(true);
				}
			});
		},
		[dispatch, getCustomPropertiesArray]
	);

	useEffect(() => {
		listCustomPropertiesHandler(defaultAccountId);
	}, [defaultAccountId, listCustomPropertiesHandler]);

	const showNewPropertyDialogBoxHandler = useCallback(() => {
		setShowNewPropertyDialog(true);
	}, []);

	const showEditPropertyDialogBoxHandler = useCallback((data: CustomProperty) => {
		setShowEditPropertyDialog(true);
		setCustomPropertyToEdit(data);
	}, []);

	const resetCurrentCustomProperties = useCallback(() => {
		setCurrentNameInEditor('');
		setCurrentNameInApi('');
		setCurrentType('string');
		setCurrentInMedia(true);
		setCurrentInCatalog(false);
		setCurrentInPlaylist(false);
		setCurrentIsRequired(false);
		setCurrentMultiSelect({ value: '', name: '' });
		setCurrentMultiSelectArray([]);
		setNameInApiExists(false);
	}, []);

	const resetCustomPropertyToEdit = useCallback(() => {
		setCustomPropertyToEdit({} as CustomProperty);
	}, []);

	const hideNewPropertyDialogHandler = useCallback(() => {
		setShowNewPropertyDialog(false);
		resetCurrentCustomProperties();
	}, [resetCurrentCustomProperties]);

	const hideEditPropertyDialogHandler = useCallback(() => {
		setShowEditPropertyDialog(false);
		resetCustomPropertyToEdit();
	}, [resetCustomPropertyToEdit]);

	const handleDeleteCustomPropertyDialogHandler = useCallback((nameInEditor: string, nameInApi: string) => {
		setCustomPropertyToDelete(nameInEditor);
		setDeleteCustomPropertyDialogStatus(true);
		setCurrentlySelectedItem(nameInApi);
	}, []);

	const handleHideDeleteCustomPropertyDialogHandler = useCallback(() => {
		setDeleteCustomPropertyDialogStatus(false);
		setCurrentlySelectedItem('');
		setCustomPropertyToDelete('');
	}, []);

	const handleCustomPropertyDelete = useCallback(() => {
		const filtered = customProperties.filter((data) => data.nameInApi !== currentlySelectedItem);
		setCustomProperties(filtered);
		setDeleteCustomPropertyDialogStatus(false);
		dispatch(updateCustomProperties(defaultAccountId, getCustomPropertiesJSON(filtered)));
	}, [customProperties, dispatch, defaultAccountId, getCustomPropertiesJSON, currentlySelectedItem]);

	const handleCustomPropertyCreation = useCallback(() => {
		const checkNameInApi = (obj: CustomProperty) => obj.nameInApi === currentNameInApi;
		if (customProperties.some(checkNameInApi)) {
			setNameInApiExists(true);
		} else {
			let result = [...customProperties];
			let tempObj: CustomProperty;
			if (Array.isArray(currentMultiSelectArray) && currentMultiSelectArray.length) {
				tempObj = {
					order: customProperties.length + 1,
					nameInEditor: currentNameInEditor,
					nameInApi: currentNameInApi,
					type: currentType,
					inMedia: currentInMedia,
					inCatalog: currentInCatalog,
					inPlaylist: currentInPlaylist,
					isRequired: currentIsRequired,
					multiSelect: currentMultiSelectArray,
				};
			} else {
				tempObj = {
					order: customProperties.length + 1,
					nameInEditor: currentNameInEditor,
					nameInApi: currentNameInApi,
					type: currentType,
					inMedia: currentInMedia,
					inCatalog: currentInCatalog,
					inPlaylist: currentInPlaylist,
					isRequired: currentIsRequired,
				};
			}

			if (tempObj.inMedia === false && tempObj.inCatalog === false && tempObj.inPlaylist === false) {
				tempObj.inMedia = true;
			}
			result.push(tempObj);

			if (isCustomPropertyCreate) {
				setShowNewPropertyDialog(false);
				setCustomProperties(result);
				dispatch(createCustomProperties(defaultAccountId, getCustomPropertiesJSON(result)));
				resetCurrentCustomProperties();
				setIsCustomPropertyCreate(false);
			} else {
				setShowNewPropertyDialog(false);
				setCustomProperties(result);
				dispatch(updateCustomProperties(defaultAccountId, getCustomPropertiesJSON(result)));
				resetCurrentCustomProperties();
			}
		}
	}, [customProperties, currentNameInApi, currentMultiSelectArray, isCustomPropertyCreate, currentNameInEditor, currentType, currentInMedia, currentInCatalog, currentInPlaylist, currentIsRequired, defaultAccountId, getCustomPropertiesJSON, resetCurrentCustomProperties, dispatch]);

	const handleMultiSelectItemDelete = useCallback(
		(value: string) => {
			const filtered = currentMultiSelectArray.filter((data) => data.value !== value);
			setCurrentMultiSelectArray(filtered);
		},
		[currentMultiSelectArray]
	);

	const handleMultiSelectItemOnEditDelete = useCallback(
		(value: string) => {
			let result = { ...customPropertyToEdit };
			let tempObj = customPropertyToEdit.multiSelect || [];
			let filtered = tempObj.filter((data) => data.value !== value);
			result.multiSelect = filtered;
			setCustomPropertyToEdit(result);
		},
		[customPropertyToEdit]
	);

	const handleNameInApiHandler = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setCurrentNameInApi(event.target.value);
	}, []);

	const handleNameInEditorHandler = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setCurrentNameInEditor(event.target.value);
	}, []);

	const handleTypeHandler = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
		setCurrentType(event.target.value);
	}, []);

	const handleInMediaHandler = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setCurrentInMedia(event.target.checked);
	}, []);

	const handleInCatalogHandler = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setCurrentInCatalog(event.target.checked);
	}, []);

	const handleInPlaylistHandler = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setCurrentInPlaylist(event.target.checked);
	}, []);

	const handleIsRequiredHandler = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setCurrentIsRequired(event.target.checked);
	}, []);

	const handleMultiSelectValueHandler = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			let tempObj = { ...currentMultiSelect };
			tempObj.value = event.target.value;
			setCurrentMultiSelect(tempObj);
		},
		[currentMultiSelect]
	);

	const handleMultiSelectNameHandler = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			let tempObj = { ...currentMultiSelect };
			tempObj.name = event.target.value;
			setCurrentMultiSelect(tempObj);
		},
		[currentMultiSelect]
	);

	const handleEditTypeHandler = useCallback(
		(event: React.ChangeEvent<HTMLSelectElement>) => {
			let tempObj = { ...customPropertyToEdit };
			tempObj.type = event.target.value;
			setCustomPropertyToEdit(tempObj);
		},
		[customPropertyToEdit]
	);

	const handleEditInMediaHandler = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			let tempObj = { ...customPropertyToEdit };
			tempObj.inMedia = event.target.checked;
			setCustomPropertyToEdit(tempObj);
		},
		[customPropertyToEdit]
	);

	const handleEditInCatalogHandler = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			let tempObj = { ...customPropertyToEdit };
			tempObj.inCatalog = event.target.checked;
			setCustomPropertyToEdit(tempObj);
		},
		[customPropertyToEdit]
	);

	const handleEditInPlaylistHandler = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			let tempObj = { ...customPropertyToEdit };
			tempObj.inPlaylist = event.target.checked;
			setCustomPropertyToEdit(tempObj);
		},
		[customPropertyToEdit]
	);

	const handleEditIsRequiredHandler = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			let tempObj = { ...customPropertyToEdit };
			tempObj.isRequired = event.target.checked;
			setCustomPropertyToEdit(tempObj);
		},
		[customPropertyToEdit]
	);

	const handleEditNameInEditorHandler = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			let tempObj = { ...customPropertyToEdit };
			tempObj.nameInEditor = event.target.value;
			setCustomPropertyToEdit(tempObj);
		},
		[customPropertyToEdit]
	);

	const resetCurrentMultiSelect = useCallback(() => {
		setCurrentMultiSelect({ value: '', name: '' });
	}, []);

	const handleAddMultiSelectHandler = useCallback(() => {
		let result = [...currentMultiSelectArray];
		let tempObj: MultiSelectItem = {
			value: currentMultiSelect.value,
			name: currentMultiSelect.name,
		};
		result.push(tempObj);
		setCurrentMultiSelectArray(result);
		resetCurrentMultiSelect();
	}, [currentMultiSelect, currentMultiSelectArray, resetCurrentMultiSelect]);

	const handleAddMultiSelectOnEditHandler = useCallback(() => {
		let result = { ...customPropertyToEdit };
		let resultArray = result.multiSelect || [];
		let tempObj: MultiSelectItem = {
			value: currentMultiSelect.value,
			name: currentMultiSelect.name,
		};
		resultArray.push(tempObj);
		result.multiSelect = resultArray;
		setCustomPropertyToEdit(result);
		setCurrentMultiSelect({ name: '', value: '' });
	}, [currentMultiSelect, customPropertyToEdit]);

	const handleCustomPropertyEdit = useCallback(() => {
		const filtered = customProperties.filter((data) => data.nameInApi !== customPropertyToEdit.nameInApi);

		if (filtered.filter((e) => e.nameInApi === customPropertyToEdit.nameInApi).length > 0) {
			setNameInApiExists(true);
		} else {
			let tempObj = [customPropertyToEdit];
			let result = customProperties.map((item) => tempObj.find((data) => data.order === item.order) || item);

			setShowEditPropertyDialog(false);
			setCustomProperties(result);
			dispatch(updateCustomProperties(defaultAccountId, getCustomPropertiesJSON(result)));
			resetCustomPropertyToEdit();
		}
	}, [customProperties, customPropertyToEdit, defaultAccountId, getCustomPropertiesJSON, resetCustomPropertyToEdit, dispatch]);

	const getTypeDisplay = (type: string) => {
		switch (type) {
			case 'string':
				return 'Text';
			case 'date':
				return 'Date';
			case 'timestamp':
				return 'Date and time';
			case 'number':
				return 'Number';
			case 'boolean':
				return 'Boolean';
			case 'multiselect':
				return 'Multi selection';
			default:
				return type;
		}
	};

    const handleCustomPropertyConfirmDelete = useCallback((e: { ok: boolean }) => {
        if (e.ok) {
            handleCustomPropertyDelete();
        } else {
            handleHideDeleteCustomPropertyDialogHandler();
        }
    }, [handleCustomPropertyDelete, handleHideDeleteCustomPropertyDialogHandler]);

	return (
		<>
			<div className="profileScreen__contentWrapper">
				<div className="profileScreen__contentHeader">
					<h3>{t('SETTING_CUSTOM_PROPERTIES')}</h3>
					<Button variant="contained" color="primary" startIcon={<Add />} onClick={showNewPropertyDialogBoxHandler}>
						{t('COMMON_NEW')}
					</Button>
				</div>

				<div className="profileScreen__contentSubHeader">
					<h4>
						{customProperties.length} {t('SETTING_CUSTOM_PROPERTIES_LOWERCASE')}
					</h4>
				</div>

				<TableContainer>
					<Table stickyHeader aria-label="Custom dictionary table">
						<TableHead>
							<TableRow>
								<TableCell className={classes.tableCell}>{t('SETTING_ORDER')}</TableCell>
								<TableCell className={classes.tableCell}>{t('COMMON_DESCRIPTION')}</TableCell>
								<TableCell className={classes.tableCell}>{t('SETTING_API_NAME')}</TableCell>
								<TableCell className={classes.tableCell}>{t('COMMON_TYPE')}</TableCell>
								<TableCell className={classes.tableCellActions}>{t('COMMON_DELETE')}</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{customProperties.map((row) => (
								<TableRow
									key={row.nameInApi}
									onClick={() => showEditPropertyDialogBoxHandler(row)}
									style={{ cursor: 'pointer' }}
								>
									<TableCell className={classes.tableCellBody}>{row.order}</TableCell>
									<TableCell className={classes.tableCellBody}>{row.nameInEditor}</TableCell>
									<TableCell className={classes.tableCellBody}>{row.nameInApi}</TableCell>
									<TableCell className={classes.tableCellBody}>{getTypeDisplay(row.type)}</TableCell>
									<TableCell className={classes.tableCellActions}>
                                        <IconButton onClick={(event) => {
                                            handleDeleteCustomPropertyDialogHandler(row.nameInEditor, row.nameInApi);
                                            event.stopPropagation();
                                        }}>
                                            <Delete color="error" />
                                        </IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</div>

			<Dialog open={showNewPropertyDialog} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
				<DialogTitle className="dialogTitle" disableTypography>
					<Typography variant="h6">{t('SETTING_ADD_CUSTOM_PROPERTY')}</Typography>
					<IconButton onClick={hideNewPropertyDialogHandler}>
						<Close />
					</IconButton>
				</DialogTitle>
				<DialogContent>
					<div className="customPropertiesDialogBoxInternalBlock">
						<label>{t('COMMON_DESCRIPTION')}</label>
						<input
							type="text"
							className="genericTextInput"
							onChange={handleNameInEditorHandler}
							value={currentNameInEditor}
							required
						/>
					</div>
					<div className="customPropertiesDialogBoxInternalBlock">
						<label>
							{t('SETTING_API_NAME')}
							<span className="alreadyExistsSpan">{nameInApiExists ? t('SETTING_ALREADY_EXISTS_IN_SCHEMA') : ''}</span>
						</label>
						<input
							type="text"
							className={nameInApiExists ? 'genericTextInput alreadyExistsInput' : 'genericTextInput'}
							onChange={handleNameInApiHandler}
							value={currentNameInApi}
							required
						/>
					</div>

					<label className="customPropertiesDialogLabel">{t('COMMON_TYPE')}</label>
					<div className="dialogContentMidSectionWrapper">
						<div className="typeDropDownWrapper">
							<select className="typeDropDown" onChange={handleTypeHandler} value={currentType}>
								<option value={'string'}>{'Text'}</option>
								<option value={'date'}>{'Date'}</option>
								<option value={'timestamp'}>{'Date and time'}</option>
								<option value={'number'}>{'Number'}</option>
								<option value={'boolean'}>{'Boolean'}</option>
								<option value={'multiselect'}>{'Multi selection'}</option>
							</select>
						</div>
						<div className="requiredCheckBoxWrapper">
							<FormControl>
								<FormGroup aria-label="position" row>
									<FormControlLabel
										value="required"
										control={
											<Checkbox
												color="primary"
												checked={currentIsRequired}
												onChange={handleIsRequiredHandler}
												value="currentIsRequired"
											/>
										}
										label={<span className="customPropertiesLabel">{t('SETTING_MAKE_REQUIRED')}</span>}
										labelPlacement="end"
									/>
								</FormGroup>
							</FormControl>
						</div>
					</div>
					<div
						className={
							currentType === 'multiselect' ? 'customPropertiesMultiSelectDialogBoxInternalBlock' : 'hideDiv'
						}
					>
						<label>{t('SETTING_ADD_TO_OPTIONS')}</label>
						<div className="customPropertiesMultiSelectInput">
							<label>{t('COMMON_VALUE')}:</label>
							<input
								type="text"
								className="genericTextInput"
								onChange={handleMultiSelectValueHandler}
								value={currentMultiSelect.value}
								required
							/>
							<label>{t('COMMON_NAME')}:</label>
							<input
								type="text"
								className="genericTextInput"
								onChange={handleMultiSelectNameHandler}
								value={currentMultiSelect.name}
								required
							/>
							<button onClick={handleAddMultiSelectHandler}>{t('COMMON_ADD')}</button>
						</div>
						{currentMultiSelectArray && currentMultiSelectArray.length > 0 ? (
							<label className="optionsLabel">{t('COMMON_OPTIONS')}</label>
						) : (
							''
						)}
						{currentMultiSelectArray &&
							currentMultiSelectArray.map((eachSet, i) => (
								<div key={i} className="multiSelectTableRow">
									<label>
										{t('COMMON_VALUE')}: {eachSet.value}
									</label>
									<label>
										{t('COMMON_NAME')}: {eachSet.name}
									</label>
									<div className="customPropertiesTableItem">
										<button
											className="deleteIconMultiSelect"
											onClick={(event) => {
												handleMultiSelectItemDelete(eachSet.value);
												event.stopPropagation();
											}}
										>
											<IconsStore width="10px" height="10px" color="red" iconName={IconsList.CLOSE} />
										</button>
									</div>
								</div>
							))}
					</div>
					<FormControl component="fieldset">
						<FormLabel className="customPropertiesDialogLabel" component="legend">
							{t('SETTING_USE_WITH')}:
						</FormLabel>
						<FormGroup aria-label="position" row>
							<FormControlLabel
								value="start"
								control={
									<Checkbox
										color="primary"
										checked={currentInMedia}
										onChange={handleInMediaHandler}
										value="currentInMedia"
									/>
								}
								label={<span className="customPropertiesLabel">{t('COMMON_MEDIA')}</span>}
								labelPlacement="end"
							/>
							<FormControlLabel
								value="bottom"
								control={
									<Checkbox
										color="primary"
										checked={currentInCatalog}
										onChange={handleInCatalogHandler}
										value="currentInCatalog"
									/>
								}
								label={<span className="customPropertiesLabel">{t('COMMON_MEDIA_TYPE_CATALOG')}</span>}
								labelPlacement="end"
							/>
							<FormControlLabel
								value="end"
								control={
									<Checkbox
										color="primary"
										checked={currentInPlaylist}
										onChange={handleInPlaylistHandler}
										value="currentInPlaylist"
									/>
								}
								label={<span className="customPropertiesLabel">{t('COMMON_MEDIA_TYPE_PLAYLIST')}</span>}
								labelPlacement="end"
							/>
						</FormGroup>
					</FormControl>
					<div className="customPropsDialogText">
						<p>{t('SETTING_CUSTOM_PROPERTIES_INFO')}</p>
					</div>
				</DialogContent>
				<DialogActions>
					<div className="dialogActionsWrapper">
						<div className="dialogBoxInternalBlock createNewUserBtnContentWrapper">
							<Button
								variant="contained"
								disabled={currentInMedia === false && currentInCatalog === false && currentInPlaylist === false}
								className="defaultActionBtn"
								onClick={handleCustomPropertyCreation}
							>
								{t('COMMON_SAVE')}
							</Button>
						</div>
					</div>
				</DialogActions>
			</Dialog>

			<Dialog
				open={showEditPropertyDialog}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				<DialogTitle className="dialogTitle" disableTypography>
					<Typography variant="h6">{t('SETTING_EDIT_CUSTOM_PROPERTY')}</Typography>
					<IconButton onClick={hideEditPropertyDialogHandler}>
						<Close />
					</IconButton>
				</DialogTitle>
				<DialogContent>
					<div className="customPropertiesDialogBoxInternalBlock">
						<label>{t('COMMON_DESCRIPTION')}</label>
						<input
							type="text"
							className="genericTextInput"
							onChange={handleEditNameInEditorHandler}
							value={customPropertyToEdit.nameInEditor || ''}
							required
						/>
					</div>
					<div className="customPropertiesDialogBoxInternalBlock">
						<label>
							{t('SETTING_API_NAME')}
							<span className="alreadyExistsSpan">{nameInApiExists ? t('SETTING_ALREADY_EXISTS_IN_SCHEMA') : ''}</span>
						</label>
						<div className="editNameInApiProp">{customPropertyToEdit.nameInApi}</div>
					</div>

					<label className="customPropertiesDialogLabel">{t('COMMON_TYPE')}</label>
					<div className="dialogContentMidSectionWrapper">
						<div className="typeDropDownWrapper">
							<select
								className="typeDropDown"
								onChange={handleEditTypeHandler}
								value={customPropertyToEdit.type || 'string'}
							>
								<option value={'string'}>{'Text'}</option>
								<option value={'date'}>{'Date'}</option>
								<option value={'timestamp'}>{'Date and time'}</option>
								<option value={'number'}>{'Number'}</option>
								<option value={'boolean'}>{'Boolean'}</option>
								<option value={'multiselect'}>{'Multi selection'}</option>
							</select>
						</div>
						<div className="requiredCheckBoxWrapper">
							<FormControl>
								<FormGroup aria-label="position" row>
									<FormControlLabel
										value="required"
										control={
											<Checkbox
												color="primary"
												checked={customPropertyToEdit.isRequired || false}
												onChange={handleEditIsRequiredHandler}
												value="customPropertyToEdit.isRequired"
											/>
										}
										label={<span className="customPropertiesLabel">{t('SETTING_MAKE_REQUIRED')}</span>}
										labelPlacement="end"
									/>
								</FormGroup>
							</FormControl>
						</div>
					</div>
					<div
						className={
							customPropertyToEdit.type === 'multiselect'
								? 'customPropertiesMultiSelectDialogBoxInternalBlock'
								: 'hideDiv'
						}
					>
						<label>{t('SETTING_ADD_TO_OPTIONS')}</label>
						<div className="customPropertiesMultiSelectInput">
							<label>{t('COMMON_NAME')}:</label>
							<input
								type="text"
								className="genericTextInput"
								onChange={handleMultiSelectNameHandler}
								value={currentMultiSelect.name}
								required
							/>
							<label>{t('COMMON_VALUE')}:</label>
							<input
								type="text"
								className="genericTextInput"
								onChange={handleMultiSelectValueHandler}
								value={currentMultiSelect.value}
								required
							/>
							<button onClick={handleAddMultiSelectOnEditHandler}>{t('COMMON_ADD')}</button>
						</div>
						{customPropertyToEdit.multiSelect && customPropertyToEdit.multiSelect.length > 0 ? (
							<label className="optionsLabel">{t('COMMON_OPTIONS')}</label>
						) : (
							''
						)}
						{customPropertyToEdit.multiSelect &&
							customPropertyToEdit.multiSelect.map((eachSet, i) => (
								<div key={i} className="multiSelectTableRow">
									<label>
										{t('COMMON_NAME')}: {eachSet.name}
									</label>
									<label>
										{t('COMMON_VALUE')}: {eachSet.value}
									</label>
									<div className="customPropertiesTableItem">
										<button
											className="deleteIconMultiSelect"
											onClick={(event) => {
												handleMultiSelectItemOnEditDelete(eachSet.value);
												event.stopPropagation();
											}}
										>
											<IconsStore width="10px" height="10px" color="red" iconName={IconsList.CLOSE} />
										</button>
									</div>
								</div>
							))}
					</div>
					<FormControl component="fieldset">
						<FormLabel className="customPropertiesDialogLabel" component="legend">
							{t('SETTING_USE_WITH')}:
						</FormLabel>
						<FormGroup aria-label="position" row>
							<FormControlLabel
								value="start"
								control={
									<Checkbox
										color="primary"
										checked={customPropertyToEdit.inMedia || false}
										onChange={handleEditInMediaHandler}
										value="customPropertyToEdit.inMedia"
									/>
								}
								label={<span className="customPropertiesLabel">{t('COMMON_MEDIA')}</span>}
								labelPlacement="end"
							/>
							<FormControlLabel
								value="bottom"
								control={
									<Checkbox
										color="primary"
										checked={customPropertyToEdit.inCatalog || false}
										onChange={handleEditInCatalogHandler}
										value="customPropertyToEdit.inCatalog"
									/>
								}
								label={<span className="customPropertiesLabel">{t('COMMON_MEDIA_TYPE_CATALOG')}</span>}
								labelPlacement="end"
							/>
							<FormControlLabel
								value="end"
								control={
									<Checkbox
										color="primary"
										checked={customPropertyToEdit.inPlaylist || false}
										onChange={handleEditInPlaylistHandler}
										value="customPropertyToEdit.inPlaylist"
									/>
								}
								label={<span className="customPropertiesLabel">{t('COMMON_MEDIA_TYPE_PLAYLIST')}</span>}
								labelPlacement="end"
							/>
						</FormGroup>
					</FormControl>
					<div className="customPropsDialogText">
						<p>{t('SETTING_CUSTOM_PROPERTIES_INFO')}</p>
					</div>
				</DialogContent>
				<DialogActions>
					<div className="dialogActionsWrapper">
						<div className="dialogBoxInternalBlock createNewUserBtnContentWrapper">
							<Button
								variant="contained"
								disabled={
									customPropertyToEdit.inMedia === false &&
									customPropertyToEdit.inCatalog === false &&
									customPropertyToEdit.inPlaylist === false
								}
								className="defaultActionBtn"
								onClick={handleCustomPropertyEdit}
							>
								{t('COMMON_SAVE_CHANGES')}
							</Button>
						</div>
					</div>
				</DialogActions>
			</Dialog>

            {deleteCustomPropertyDialogStatus && <ConfirmationDialog
                title={t('SETTING_LABEL_ARE_YOU_SURE_TO_DELETE_CUSTOM_PROPERTY')}
                message={customPropertyToDelete}
                okButtonText={t('COMMON_DELETE')}
                cancelButtonText={t('COMMON_BACK')}
                callback={handleCustomPropertyConfirmDelete}
            ></ConfirmationDialog>}
		</>
	);
};

export default CustomProperties;
